import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../ui';
import { REPORT_DEFINITIONS } from './reportDefinitions';
import ReportDetail from './ReportDetail';
import ReportsOverview from './ReportsOverview';
import ReportsLibrary from './ReportsLibrary';
import Tabs from '../ui/Tabs';

/**
 * ReportsPage — top-level container per RPT-1 wireframe.
 *
 * Single tab bar: Overview | Library. No nested tabs.
 * - Overview: KPIs + Revenue Trends + Material Usage charts
 * - Library: grid of canned reports
 * When a canned report is opened, ReportDetail takes over the full page.
 */
export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Read ?report= from URL to open a specific canned report
  const selectedReportId = searchParams.get('report');
  const selectedReport = useMemo(
    () => Object.values(REPORT_DEFINITIONS).find((r) => r.id === selectedReportId),
    [selectedReportId]
  );

  // Period selector state (Overview page date range).
  // Value is a preset key ('this_month', 'last_quarter', etc.) matching
  // PeriodSelector's PERIOD_PRESETS.
  const [period, setPeriod] = useState('this_month');

  // Set selected report by id (updates URL param and clears when null)
  const setSelectedReportById = (reportOrId) => {
    // Accept either a report id string or a full report object (ReportsLibrary
    // passes the object; ReportsOverview / switcher pass the id).
    const id = reportOrId && typeof reportOrId === 'object' ? reportOrId.id : reportOrId;
    if (id) {
      setSearchParams({ report: id });
    } else {
      searchParams.delete('report');
      setSearchParams(searchParams);
    }
  };

  const handleBack = () => setSelectedReportById(null);

  // Build the "all reports" list for the global switcher used in ReportDetail
  const allReportsForSwitcher = useMemo(
    () => Object.values(REPORT_DEFINITIONS).map((r) => ({ value: r.id, label: r.title })),
    []
  );

  // Deep-link into a canned report: render ReportDetail full-page
  if (selectedReport) {
    return (
      <ReportDetail
        reportConfig={selectedReport}
        onBack={handleBack}
        onSwitchReport={setSelectedReportById}
        allReports={allReportsForSwitcher}
        period={period}
      />
    );
  }

  return (
    <div className="px-6 space-y-4">
      {/* Header: title only. Period picker + Download live INSIDE the Overview
          tab body (operator-requested: DatePicker floats left, Download floats
          right, both scoped to the Overview panel). */}
      <PageHeader title="Reports" />

      {/* Single tab row: Overview | Library (per RPT-1 wire) */}
      <Tabs
        className="mt-2"
        tabs={[
          { value: 'overview', label: 'Overview' },
          { value: 'library', label: 'Library' },
        ]}
        value={activeTab}
        onChange={setActiveTab}
      />

      <div className="mt-4">
        {activeTab === 'overview' && (
          <ReportsOverview
            period={period}
            onPeriodChange={setPeriod}
            onSelectReport={setSelectedReportById}
          />
        )}
        {activeTab === 'library' && (
          <ReportsLibrary onSelectReport={setSelectedReportById} />
        )}
      </div>
    </div>
  );
}
